///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import {
    BomDeviceDTO,
    BomDeviceSectorDTO,
    GtthKeyValueDTO,
    ProductAccessoryTypeEnum,
    ProductDeviceTypeEnum
} from '../../../../generated/shared-types.model';
import { isDefined } from '@siklu/sui-lib/utils';

export class ExtendedBomDeviceDTO implements BomDeviceDTO {
    private constructor(
        public type: number,
        public baseProductName: string,
        public secondNodeName: string,
        public antenna: number,
        public azimuth: number,
        public deviceType: ProductDeviceTypeEnum,
        public rxFrequency: number,
        public txFrequency: number,
        public isSectorBu: boolean,
        public sectorIndex: number,
        public installed: boolean,
        public userForced: boolean,
        public availableRadios: GtthKeyValueDTO[],
        public available: Record<number, GtthKeyValueDTO[]>,
        public notes: string,
        public layerIndex: number,
        //product
        public typeProductId: number,
        public typeName: string,
        // antenna
        public antennaAccessoryId: number,
        public antennaName: string,
        // mk
        public mountingKitId: number,
        public mountingKit: string,
        // software upgrade
        public capacityLicenseId: number,
        public capacityLicense: string,
        // poe
        public poeId: number,
        public poe: string,
        // srg
        public srgId: number,
        public srg: string,
        public srgQuantity: number,
        // pse
        public pseId: number,
        public pse: string,
        // sfp
        public sfpId: number,
        public sfp: string,
        // aes
        public aesId: number,
        public aes: string,
        // extendmm
        public extendmmId: number,
        public extendmm: string,
        // l2
        public l2Id: number,
        public l2: string,
        // sync
        public syncId: number,
        public sync: string,
        // sector in device
        public deviceSectorsConfig: BomDeviceSectorDTO[],
        public deviceAN: string,
        public deviceIP: string,
        public ptP: boolean
    ) {
        if (!available) {
            this.available = {};
        }
    }

    static fromDeviceDto(device: BomDeviceDTO): ExtendedBomDeviceDTO {
        return new ExtendedBomDeviceDTO(
            device.type,
            device.baseProductName,
            device.secondNodeName,
            device.antenna,
            device.azimuth,
            device.deviceType,
            device.rxFrequency,
            device.txFrequency,
            device.isSectorBu,
            device.sectorIndex,
            device.installed,
            device.userForced,
            [],
            {},
            device.notes,
            device.layerIndex,
            device.typeProductId,
            device.typeName,
            device.antennaAccessoryId,
            device.antennaName,
            device.mountingKitId,
            device.mountingKit,
            device.capacityLicenseId,
            device.capacityLicense,
            device.poeId,
            device.poe,
            device.srgId,
            device.srg,
            device.srgQuantity,
            device.pseId,
            device.pse,
            device.sfpId,
            device.sfp,
            device.aesId,
            device.aes,
            device.extendmmId,
            device.extendmm,
            device.l2Id,
            device.l2,
            device.syncId,
            device.sync,
            device.deviceSectorsConfig,
            device.deviceAN,
            device.deviceIP,
            device.ptP
        );
    }

    static empty() {
        return new ExtendedBomDeviceDTO(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            [],
            {},
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            [],
            undefined,
            undefined,
            undefined
        );
    }

    mergeWithDeviceDto(device: BomDeviceDTO, available: Record<number, GtthKeyValueDTO[]>, force: boolean): void {
        if (force) {
            this.typeProductId = device.typeProductId;
            this.typeName = device.typeName;
            this.baseProductName = device.baseProductName;
        } else {
            this.typeProductId = this.typeProductId ? this.typeProductId : device.typeProductId;
            this.typeName = this.typeName ? this.typeName : device.typeName;
            this.baseProductName = this.baseProductName ? this.baseProductName : device.baseProductName;
        }

        this.setOrDefault(device, 'antennaAccessoryId', 'antennaName', available[ProductAccessoryTypeEnum.ANTENNA], force);
        this.setOrDefault(device, 'mountingKitId', 'mountingKit', available[ProductAccessoryTypeEnum.MK], force);
        this.setOrDefault(device, 'capacityLicenseId', 'capacityLicense', available[ProductAccessoryTypeEnum.SOFTWARE_UPGRADE], force);
        this.setOrDefault(device, 'poeId', 'poe', available[ProductAccessoryTypeEnum.POWER_INJECTORS], force);
        this.setOrDefault(device, 'srgId', 'srg', available[ProductAccessoryTypeEnum.SRG], force);
        if (force || !isDefined(this.srgQuantity)) {
            this.srgQuantity = isDefined(device.srgQuantity) ? device.srgQuantity : 1;
        }
        this.setOrDefault(device, 'pseId', 'pse', available[ProductAccessoryTypeEnum.OPT_PSE], force);
        this.setOrDefault(device, 'sfpId', 'sfp', available[ProductAccessoryTypeEnum.SFP], force);
        this.setOrDefault(device, 'aesId', 'aes', available[ProductAccessoryTypeEnum.OPT_AES], force);
        this.setOrDefault(device, 'extendmmId', 'extendmm', available[ProductAccessoryTypeEnum.OPT_EXTENDMM], force);
        this.setOrDefault(device, 'l2Id', 'l2', available[ProductAccessoryTypeEnum.OPT_L2], force);
        this.setOrDefault(device, 'syncId', 'sync', available[ProductAccessoryTypeEnum.OPT_SYNC], force);

        if (this.deviceSectorsConfig?.length > 0) {
            for (let i = 0; i < this.deviceSectorsConfig.length; i++) {
                this.setSectorConfigOrDefault(
                    device,
                    i,
                    'sectorLicenseId',
                    'sectorLicense',
                    available[ProductAccessoryTypeEnum.SECTOR_LICENSE],
                    force
                );
                this.setSectorConfigOrDefault(
                    device,
                    i,
                    'capacityLicenseId',
                    'capacityLicense',
                    available[ProductAccessoryTypeEnum.SOFTWARE_UPGRADE],
                    force
                );
            }
        }
        this.notes = device.notes;
    }

    getNeighboursNames(): string[] {
        return this.secondNodeName?.length ? this.secondNodeName.split(',').map((value) => value.trim()) : [];
    }

    private setOrDefault(device: BomDeviceDTO, keyName: string, valueName: string, available: GtthKeyValueDTO[], force: boolean) {
        if (force || !this.isValueValid(this[keyName], this[valueName], available)) {
            const validValue = this.checkValue(device[keyName], device[valueName], available);
            this[keyName] = validValue.key;
            this[valueName] = validValue.value;
        }
    }

    private setSectorConfigOrDefault(
        device: BomDeviceDTO,
        sectorInDevice: number,
        keyName: string,
        valueName: string,
        available: GtthKeyValueDTO[],
        force: boolean
    ) {
        if (
            force ||
            !this.isValueValid(
                this.deviceSectorsConfig[sectorInDevice][keyName],
                this.deviceSectorsConfig[sectorInDevice][valueName],
                available
            )
        ) {
            const validValue = this.checkValue(device[keyName], device[valueName], available);
            this.deviceSectorsConfig[sectorInDevice][keyName] = validValue.key;
            this.deviceSectorsConfig[sectorInDevice][valueName] = validValue.value;
        }
    }

    private checkValue(id: number, name: string, available: GtthKeyValueDTO[]): GtthKeyValueDTO {
        const byId = available.find((value) => value.key === id);
        if (byId && byId.value === name) {
            return byId;
        }

        const byName = available.find((value) => value.value === name);
        if (byName) {
            return byName;
        }

        return available[0];
    }

    private isValueValid(id: number, name: string, available: GtthKeyValueDTO[]): boolean {
        const byId = available.find((value) => value.key === id);
        return byId && byId.value === name;
    }
}
